import jsPDF from 'jspdf';
import 'jspdf-autotable';
import * as XLSX from 'xlsx';

/**
 * ReportService handles the generation and download of PDF and Excel reports.
 */
class ReportService {
  /**
   * Generates a PDF report with a table.
   * @param {string} title - The title of the report.
   * @param {Array} columns - Array of column objects { header: 'Name', dataKey: 'name', width: 20 }.
   * @param {Array} data - Array of data objects.
   * @param {string} fileName - The name of the file to be saved.
   * @param {string} orientation - 'portrait' or 'landscape'.
   */
  static generatePDF(title, columns, data, fileName = 'report.pdf', orientation = 'p') {
    const doc = new jsPDF({
      orientation: orientation === 'landscape' ? 'l' : 'p',
      unit: 'mm',
      format: 'a4'
    });
    const date = new Date().toLocaleDateString();
    const pageWidth = doc.internal.pageSize.getWidth();

    // Add Header
    doc.setFontSize(20);
    doc.setTextColor(27, 108, 168); // --primary-blue
    doc.text(title, 14, 22);
    
    doc.setFontSize(10);
    doc.setTextColor(100);
    doc.text(`Generated on: ${date}`, 14, 30);
    
    doc.setDrawColor(200);
    doc.line(14, 35, pageWidth - 14, 35);

    // Prepare column styles
    const columnStyles = {};
    columns.forEach((col, index) => {
      if (col.width) {
        columnStyles[index] = { cellWidth: col.width };
      }
    });

    // Add Table
    doc.autoTable({
      startY: 40,
      head: [columns.map(col => col.header)],
      body: data.map(item => columns.map(col => item[col.dataKey])),
      columnStyles: columnStyles,
      headStyles: {
        fillColor: [27, 108, 168],
        textColor: [255, 255, 255],
        fontSize: orientation === 'landscape' ? 8 : 10,
        fontStyle: 'bold',
        halign: 'center',
      },
      alternateRowStyles: {
        fillColor: [245, 248, 250],
      },
      margin: { top: 40, left: 14, right: 14 },
      styles: {
        fontSize: orientation === 'landscape' ? 7 : 9,
        cellPadding: orientation === 'landscape' ? 1.5 : 3,
        valign: 'middle',
        overflow: 'linebreak',
        halign: 'center',
      },
      // Left align the first column (usually Name)
      didParseCell: (data) => {
        if (data.column.index === 0) {
          data.cell.styles.halign = 'left';
        }
      }
    });

    doc.save(fileName);
  }

  /**
   * Generates an Excel report.
   * @param {Array} data - Array of data objects.
   * @param {string} fileName - The name of the file to be saved.
   * @param {Array} headers - Optional array of property keys to define column order.
   */
  static generateExcel(data, fileName = 'report.xlsx', headers = null) {
    const worksheet = XLSX.utils.json_to_sheet(data, headers ? { header: headers } : {});
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Report');
    XLSX.writeFile(workbook, fileName);
  }

  /**
   * Generates a PDF for an individual profile.
   * @param {string} title - Profile Title (e.g. "Student Profile").
   * @param {Object} sections - Object containing sections of info.
   * @param {string} fileName - The name of the file to be saved.
   */
  static generateProfilePDF(title, sections, fileName = 'profile.pdf') {
    const doc = new jsPDF();
    const date = new Date().toLocaleDateString();

    // Header
    doc.setFontSize(22);
    doc.setTextColor(27, 108, 168);
    doc.text(title, 14, 25);
    
    doc.setFontSize(10);
    doc.setTextColor(100);
    doc.text(`Exported on: ${date}`, 14, 32);
    
    doc.setDrawColor(27, 108, 168);
    doc.setLineWidth(1);
    doc.line(14, 38, 196, 38);

    let currentY = 50;

    Object.entries(sections).forEach(([sectionTitle, fields]) => {
      // Section Heading
      doc.setFontSize(14);
      doc.setTextColor(50);
      doc.setFont('helvetica', 'bold');
      doc.text(sectionTitle.toUpperCase(), 14, currentY);
      currentY += 8;

      // Fields
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(80);

      Object.entries(fields).forEach(([label, value]) => {
        if (currentY > 270) {
          doc.addPage();
          currentY = 20;
        }
        
        doc.setFont('helvetica', 'bold');
        doc.text(`${label}:`, 14, currentY);
        doc.setFont('helvetica', 'normal');
        doc.text(`${value || 'N/A'}`, 60, currentY);
        currentY += 7;
      });

      currentY += 10;
    });

    doc.save(fileName);
  }

  /**
   * Helper for Staff Profile PDF
   */
  static generateStaffProfilePDF(staff) {
    const sections = {
      'Personal Details': {
        'Full Name': staff.name,
        'Employee ID': staff.employeeId,
        'Designation': staff.designation,
        'Email': staff.email,
        'Phone': staff.phone,
        'Date of Birth': staff.dob ? new Date(staff.dob).toLocaleDateString() : 'N/A',
        'Gender': staff.gender,
      },
      'Professional Details': {
        'Experience': `${staff.experienceInYears} Years`,
        'Academic Qualification': staff.academicQualification,
        'Joining Date': staff.joiningDate ? new Date(staff.joiningDate).toLocaleDateString() : 'N/A',
        'Status': staff.status,
      }
    };
    this.generateProfilePDF('Staff Profile', sections, `staff_${staff.employeeId || staff.name}.pdf`);
  }

  /**
   * Helper for Student Profile PDF
   */
  static generateStudentProfilePDF(student) {
    const sections = {
      'Personal Details': {
        'Full Name': student.name,
        'Reg No': student.regNo,
        'Roll No': student.rollNo,
        'Date of Birth': student.dob ? new Date(student.dob).toLocaleDateString() : 'N/A',
        'Gender': student.gender,
        'Religion': student.religion,
        'Caste': student.caste,
      },
      'Academic Details': {
        'Class': student.class?.name || 'N/A',
        'Section': student.class?.section || 'N/A',
        'Admission Date': student.admissionDate ? new Date(student.admissionDate).toLocaleDateString() : 'N/A',
      },
      'Contact Details': {
        'Phone': student.phone,
        'Email': student.email,
        'Address': student.address,
      }
    };
    this.generateProfilePDF('Student Profile', sections, `student_${student.regNo || student.name}.pdf`);
  }
}

export default ReportService;
